import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmtCr, fmtDateShort, downloadRows } from "@/lib/stock";
import { StockTiles, AgeingLadder, FreshnessBanner, StockFilters, MixTable, TH } from "@/components/StockBits";
import { AgeingSummary } from "@/components/AgeingSummary";
import { RetailExceptions } from "@/components/RetailExceptions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download } from "lucide-react";

const ZONE_COLORS = ["#0f172a", "#BB162B", "#0369a1", "#b45309", "#15803d", "#7c3aed"];

function AvrTrend({ trend, zones }) {
  if (!trend?.length) return null;
  const data = trend.map((p) => ({ ...p, day: fmtDateShort(p.date) }));
  return (
    <div className="border bg-card rounded-md p-4" data-testid="stock-avr-trend">
      <h3 className="font-heading text-sm font-semibold mb-2">Aged value at risk — last 90 days <span className="text-muted-foreground font-normal text-xs">one point per dump</span></h3>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e9f0" />
          <XAxis dataKey="day" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtCr(v).replace("₹", "")} width={64} />
          <Tooltip formatter={(v) => fmtCr(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="avr" name="Group AVR" stroke="#BB162B" strokeWidth={2.5} dot={{ r: 3 }} />
          {(zones || []).map((z, i) => <Line key={z.name} type="monotone" dataKey={z.name} name={z.name} stroke={ZONE_COLORS[(i + 2) % ZONE_COLORS.length]} strokeWidth={1.5} dot={false} strokeDasharray="4 3" />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ZoneCards({ zones }) {
  if (!zones?.length) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="stock-zone-cards">
      {zones.map((z) => (
        <div key={z.name} className="border bg-card rounded-md p-3" data-testid={`stock-zone-${z.name}`}>
          <div className="flex items-baseline justify-between"><b className="text-sm">{z.name}</b><span className="text-[10px] text-muted-foreground">{z.codes.join(" · ") || "no outlets"}</span></div>
          <div className="flex items-baseline gap-2 mt-1"><span className="num text-xl font-bold text-red-700">{fmtCr(z.avr)}</span><span className="text-[11px] text-muted-foreground">AVR · {z.pct_aged_value ?? 0}% of {fmtCr(z.value)}</span></div>
          <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
            <span>{z.units} units · {z.aged_units} aged</span>
            <span className="text-emerald-700">liquidated MTD {z.liquidated_mtd?.units ?? 0}</span>
            {z.avr_delta7 != null && <span className={z.avr_delta7 > 0 ? "text-red-600" : "text-emerald-700"}>{z.avr_delta7 > 0 ? "▲" : "▼"} {fmtCr(Math.abs(z.avr_delta7))} / 7 d</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

const heatBg = (pct) => (pct >= 25 ? "bg-red-200 text-red-900" : pct >= 15 ? "bg-orange-100 text-orange-900" : pct >= 8 ? "bg-amber-50 text-amber-900" : pct > 0 ? "bg-emerald-50 text-emerald-900" : "");

function HeatGrid({ heat, buckets }) {
  const [sort, setSort] = useState("avr");
  const rows = [...heat].sort((a, b) => (sort === "pct" ? b.pct_aged_value - a.pct_aged_value : sort === "units" ? b.units - a.units : b.avr - a.avr));
  const exportCsv = () => downloadRows("stock_branch_heat.csv", ["Branch", "Name", "Units", "Value", ...buckets.map((b) => b.label), "Aged units", "AVR", "% aged value", "Oldest"], rows.map((r) => [r.dealer_code, r.dealer_name, r.units, r.value, ...buckets.map((b) => r.cells[b.label]?.units ?? 0), r.aged_units, r.avr, r.pct_aged_value, r.oldest]));
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="stock-heat-grid">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3 sticky top-0 bg-card z-10">
        <h3 className="font-heading text-sm font-semibold">Branch heat-grid <span className="text-muted-foreground font-normal text-xs">units per bucket · colour = % aged value · click a branch</span></h3>
        <div className="ml-auto flex items-center gap-1 text-[11px]">
          {[["avr", "AVR"], ["pct", "% aged"], ["units", "Units"]].map(([k, l]) => <button key={k} onClick={() => setSort(k)} className={`rounded border px-2 py-0.5 ${sort === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`stock-heat-sort-${k}`}>{l}</button>)}
          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={exportCsv} data-testid="stock-heat-export"><Download size={12} /></Button>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr><th className={TH}>Branch</th><th className={`${TH} text-right`}>Units</th>{buckets.map((b) => <th key={b.label} className={`${TH} text-right`}><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: b.color }} />{b.label}</th>)}<th className={`${TH} text-right`}>AVR</th><th className={`${TH} text-right`}>% aged ₹</th><th className={`${TH} text-right`}>Oldest</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.dealer_code} className={`border-b last:border-0 hover:bg-muted/50 ${heatBg(r.pct_aged_value)}`} data-testid={`stock-heat-${r.dealer_code}`}>
              <td className="px-2 py-1.5 whitespace-nowrap"><Link to={`/stock/branch/${r.dealer_code}`} className="font-semibold hover:underline">{r.dealer_code}</Link> <span className="text-muted-foreground">{r.dealer_name}</span>{r.in_transit > 0 && <span className="ml-1 text-[9px] text-muted-foreground">({r.in_transit} transit)</span>}</td>
              <td className="px-2 py-1.5 text-right num font-semibold">{r.units}</td>
              {buckets.map((b) => { const c = r.cells[b.label]; return <td key={b.label} className="px-2 py-1.5 text-right num" title={fmtCr(c?.value)}>{c?.units || <span className="text-muted-foreground/50">·</span>}</td>; })}
              <td className="px-2 py-1.5 text-right num font-bold">{fmtCr(r.avr)}</td>
              <td className="px-2 py-1.5 text-right num">{r.pct_aged_value}%</td>
              <td className="px-2 py-1.5 text-right num text-muted-foreground">{r.oldest ?? "—"} d</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={buckets.length + 5} className="px-3 py-6 text-center text-muted-foreground">No stock in this view</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function StockCommand() {
  const { user } = useApp();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ as_of: "", dealer: "", model: "", fuel: "", colour: "", bank: "" });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { api.get("/stock/meta").then(({ data }) => setMeta(data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => {
    if (user?.role === "manager") return;
    api.get(`/stock/command?${qs(f)}`).then(({ data }) => { setData(data); setErr(null); }).catch((e) => setErr(apiError(e)));
  }, [f, user]);

  if (user?.role === "manager") return <Navigate to={`/stock/branch/${(user.dealer_codes || [])[0] || ""}`} replace />;
  const scopeLabel = user?.role === "zonal" ? "Zone view" : "Group view";
  return (
    <div className="space-y-4" data-testid="stock-command">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">Stock & Ageing <span className="text-muted-foreground font-medium text-base">· {scopeLabel}</span></h1>
        <div className="flex items-center gap-2 text-xs">
          <Link to="/stock/velocity" className="rounded border px-2.5 py-1.5 hover:bg-secondary transition-colors duration-200" data-testid="stock-goto-velocity">Retail velocity</Link>
          <Link to="/stock/locator" className="rounded border px-2.5 py-1.5 hover:bg-secondary transition-colors duration-200" data-testid="stock-goto-locator">Stock locator</Link>
          {(meta?.can_upload || meta?.can_upload_retail) && <Link to="/stock/upload" className="rounded border px-2.5 py-1.5 hover:bg-secondary transition-colors duration-200" data-testid="stock-goto-upload">{meta.can_upload ? "Upload feeds" : "Upload retail feed"}</Link>}
        </div>
      </div>
      <StockFilters meta={meta} f={f} setF={setF} />
      {err && <div className="text-sm text-red-600" data-testid="stock-error">{err}</div>}
      {!data && !err && <div className="text-sm text-muted-foreground py-20 text-center">Loading stock position…</div>}
      {data && (
        <>
          <FreshnessBanner d={data} />
          <StockTiles t={data.tiles} buckets={data.ladder} />
          <div className="grid lg:grid-cols-3 gap-4">
            <AgeingLadder ladder={data.ladder} />
            <div className="lg:col-span-2"><AvrTrend trend={data.trend} zones={data.zones} /></div>
          </div>
          <ZoneCards zones={data.zones} />
          <AgeingSummary query={f} locatorBase={f.dealer ? { dealer: f.dealer } : {}} />
          <RetailExceptions code={f.dealer || undefined} />
          <HeatGrid heat={data.heat} buckets={data.ladder} />
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            <MixTable rows={data.mix_model} label="model" testId="stock-mix-model" />
            <MixTable rows={data.mix_colour} label="colour" testId="stock-mix-colour" />
            <MixTable rows={data.mix_fuel} label="fuel" testId="stock-mix-fuel" />
            <MixTable rows={data.mix_bank} label="bank" testId="stock-mix-bank" />
          </div>
          <p className="text-[11px] text-muted-foreground">Action threshold {data.policy.action_days} days · watch from {data.policy.watch_days} days{Object.keys(data.policy.model_overrides || {}).length > 0 && <span> · overrides: {Object.entries(data.policy.model_overrides).map(([m, d]) => `${m} ${d} d`).join(", ")}</span>} · carrying cost uses each bank's rate and free-credit days (Admin › Stock) · days of supply = stock ÷ retail per day over the last 3 months of the retail feed.</p>
        </>
      )}
    </div>
  );
}
